import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  pageTitleClass,
  pageSubtitleClass,
  cardClass,
  cardBodyClass,
  badgeClass,
  formLabelClass,
  formControlClass,
  alertErrorClass,
  alertInfoClass,
  spinnerClass,
  tableClass,
  tableHeadClass,
  tableHeaderCellClass,
  tableBodyClass,
  tableRowClass,
  tableCellClass,
  tableActionsCellClass,
  btnSecondaryClass,
  modalOverlayClass,
  modalPanelClass,
  modalHeaderClass,
  modalBodyClass,
  modalFooterClass,
  modalCloseClass,
  statsGridClass,
} from "../../dashboardStyles";
import {
  GetPlatformCompaniesAction,
  GetPlatformCompanyAction,
  GetAdminsAction,
  AssignAdminAction,
  SetDirectoryListingAction,
  StartImpersonationAction,
  beginImpersonation,
  getUserInfoFromToken,
} from "../../../../store/action";
import { UserRole } from "../../../../constants/roles";
import { loadPage } from "../../../../utils/pageLoad";
import { notify, notifyFailure } from "../../../../utils/notify";
import {
  PlanNameKeys,
  DirectoryListing,
} from "../../../../constants/subscription";

/**
 * The licence states a "needs attention" filter should surface.
 *
 * The states themselves come from the API — the threshold that decides them
 * lives in `LicenceState` on the server, because the renewals summary on the
 * dashboard counts the same businesses and a rule written twice eventually
 * disagrees with itself. All this file decides is the colour.
 */
const NEEDS_ATTENTION = new Set(["problem", "expired"]);

/** How many companies a page shows. The server caps anything larger at 100. */
const PAGE_SIZE = 25;

/**
 * The businesses on the platform.
 *
 * Read-only by design for an Admin: they can see how a company they look after
 * is doing, and reach whoever runs it, but not change how it works. Support
 * requests already have their own channel, and a platform admin editing a
 * salon's services behind its owner's back is not support.
 *
 * The one exception is admin assignment, which is a SystemOwner's own decision
 * about who covers what. It used to live in a modal on the Support page, which
 * is where it was first needed rather than where it belongs.
 *
 * A SystemOwner can also open a business as its owner sees it ("Simulate") to
 * set it up on their behalf — the wizard, services, team, calendar, site. That
 * is the platform owner's call and stays theirs: an Admin gets no button, and
 * the API refuses them too.
 */
const Companies = () => {
  const { t, i18n } = useTranslation();

  const role = getUserInfoFromToken()?.role;
  const isSystemOwner = role === UserRole.SYSTEM_OWNER;

  const [companies, setCompanies] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [impersonatingId, setImpersonatingId] = useState(null);

  // The drill-down. `detail` is null while it loads so the modal can show a
  // spinner rather than the previous company's figures.
  const [openFor, setOpenFor] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailFailed, setDetailFailed] = useState(false);

  // Filtering is done here rather than in the query. The endpoint already
  // returns the caller's whole list — that is what the totals on the dashboard
  // are counted from — so filtering in the browser is instant and costs no round
  // trip. Push it to the API when a single admin looks after enough companies
  // that the list itself becomes the problem.
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Seeded from the query string so the dashboard's renewal figures can link
  // straight to the businesses they counted: "3 expired" is only useful if
  // clicking it shows those three.
  const [searchParams] = useSearchParams();
  const [licenceFilter, setLicenceFilter] = useState(
    () => searchParams.get("licence") ?? "all",
  );

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      // The admin picker is a system owner's tool; an admin gets the list alone.
      const [result, adminList] = await Promise.all([
        GetPlatformCompaniesAction({
          page,
          pageSize: PAGE_SIZE,
          search: debouncedSearch,
          status: statusFilter,
          licence: licenceFilter,
        }),
        isSystemOwner ? GetAdminsAction() : Promise.resolve([]),
      ]);
      setCompanies(Array.isArray(result?.items) ? result.items : []);
      setTotalCount(result?.totalCount ?? 0);
      setTotalPages(result?.totalPages ?? 0);
      setAdmins(Array.isArray(adminList) ? adminList : []);
    } catch (err) {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [isSystemOwner, page, debouncedSearch, statusFilter, licenceFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // Typing is not a request. Without this every keystroke would ask the server
  // for a new page while the admin is still halfway through a company's name.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Any change to what is being asked for puts us back on the first page —
  // otherwise a filter that narrows to two companies leaves you looking at
  // page 7 of nothing.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, licenceFilter]);

  const openCompany = async (company) => {
    setOpenFor(company);
    setDetail(null);
    setDetailFailed(false);
    try {
      setDetail(await GetPlatformCompanyAction(company.id));
    } catch (err) {
      setDetailFailed(true);
    }
  };

  const assign = async (organizationId, value) => {
    const adminUserId = value === "" ? null : Number(value);

    // Optimistic: the select is the only thing that changed, and reloading the
    // whole table to move one dropdown would make it jump under the cursor.
    setCompanies((previous) =>
      previous.map((c) =>
        c.id === organizationId
          ? {
              ...c,
              assignedAdminId: adminUserId,
              assignedAdminName: adminName(adminUserId),
            }
          : c,
      ),
    );

    try {
      await AssignAdminAction(organizationId, adminUserId);
      notify.success(t("Dashboard.Platform.companies.assigned"));
    } catch (err) {
      notifyFailure(err, t("Dashboard.Platform.companies.assignError"));
      load(); // put the table back to whatever the server actually holds
    }
  };

  /**
   * Moves a business in or out of the public directory.
   *
   * Optimistic, like the admin assignment above and for the same reason: the
   * answer is never in doubt, and a select that waits for a round trip before
   * showing the value you picked feels broken. A failure puts the table back to
   * whatever the server actually holds.
   *
   * `isListedInDirectory` is recomputed here rather than guessed: switching to
   * Always makes a business visible immediately, Never hides it immediately,
   * and Automatic hands the answer back to whether they are paying — which only
   * the server knows, so that case reloads.
   */
  const changeDirectoryListing = async (organizationId, listing) => {
    const previous = companies;

    setCompanies((current) =>
      current.map((c) =>
        c.id === organizationId
          ? {
              ...c,
              directoryListing: listing,
              isListedInDirectory:
                listing === DirectoryListing.ALWAYS_LISTED
                  ? true
                  : listing === DirectoryListing.NEVER_LISTED
                    ? false
                    : c.isListedInDirectory,
            }
          : c,
      ),
    );

    try {
      await SetDirectoryListingAction(organizationId, listing);
      notify.success(t("Dashboard.Platform.companies.directory.saved"));
      if (listing === DirectoryListing.AUTOMATIC) load();
    } catch (err) {
      setCompanies(previous);
      notifyFailure(err, t("Dashboard.Platform.companies.directory.error"));
    }
  };

  /**
   * Open a business as its owner. The platform session is set aside, not
   * replaced, and the banner on every page brings it back.
   *
   * A full page load into the business rather than a client-side navigation:
   * the whole dashboard — menu, route guards, setup gating — reads the token
   * once on mount, and all of it has to start again as the owner.
   */
  const impersonate = async (company) => {
    setImpersonatingId(company.id);
    try {
      const session = await StartImpersonationAction(company.id);
      beginImpersonation(session);
      loadPage("/dashboard/organization");
    } catch (err) {
      notifyFailure(err, t("Dashboard.Platform.companies.simulateError"));
      setImpersonatingId(null);
    }
  };

  const adminName = (id) => {
    const match = admins.find((a) => a.id === id);
    return match ? `${match.firstName} ${match.lastName}`.trim() : null;
  };

  const formatDate = (value) =>
    value ? new Date(value).toLocaleDateString(i18n.language) : "—";

  /**
   * A business is either offline, still being set up, or open. Derived here
   * rather than sent by the API, because it is a label rather than a fact.
   */
  const status = (company) => {
    if (company.isDeactivated) {
      return { key: "deactivated", className: "bg-red-50 text-red-700" };
    }
    if (!company.isSetupComplete) {
      return { key: "pendingSetup", className: "bg-amber-50 text-amber-700" };
    }
    return { key: "active", className: "bg-brand-50 text-brand-700" };
  };

  const LICENCE_BADGE = {
    problem: "bg-red-50 text-red-700",
    expired: "bg-red-50 text-red-700",
    expiring: "bg-amber-50 text-amber-700",
    ok: "bg-brand-50 text-brand-700",
    unbilled: "bg-ink-100 text-ink-500",
  };

  // Whatever the server sent back for the page and filters currently asked for.
  //
  // The search and both filters used to be applied here, across every company
  // on the platform. That only worked while the platform was small: at ten
  // thousand businesses the response alone was 5.8 MB and the table rendered all
  // of them. They now run in the database, so this is the page and nothing else.
  const filtered = companies;

  const filtersApplied =
    search.trim() !== "" || statusFilter !== "all" || licenceFilter !== "all";

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setLicenceFilter("all");
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className={pageTitleClass}>
          {t("Dashboard.Platform.companies.title")}
        </h1>
        <p className={pageSubtitleClass}>
          {t("Dashboard.Platform.companies.subtitle")}
        </p>
      </div>

      {failed && (
        <div className={alertErrorClass} role="alert">
          <i className="bi bi-exclamation-triangle" aria-hidden="true" />
          <span>{t("Dashboard.Platform.companies.loadError")}</span>
        </div>
      )}


      {loading ? (
        <div className="flex min-h-[40vh] items-center justify-center">
          <div
            role="status"
            aria-label={t("Dashboard.Menu.loading")}
            className={spinnerClass}
          />
        </div>
      ) : /* An empty page and an empty platform are different situations, and
             telling them apart matters more now that the server does the
             filtering: a search that matches nothing comes back with no rows,
             and calling that "there are no companies" would also hide the
             search box that produced it, leaving no way back. Only an
             unfiltered, genuinely empty result is an empty platform. */
      totalCount === 0 && !filtersApplied ? (
        <div className={alertInfoClass}>
          <i className="bi bi-info-circle" aria-hidden="true" />
          <span>
            {isSystemOwner
              ? t("Dashboard.Platform.companies.emptyPlatform")
              : t("Dashboard.Platform.companies.emptyAssigned")}
          </span>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className={cardClass}>
            <div className="flex flex-col gap-3 p-4 md:flex-row md:items-end">
              <div className="min-w-0 flex-1">
                <label
                  className={formLabelClass}
                  htmlFor="companies-search"
                >
                  {t("Dashboard.Platform.companies.filters.search")}
                </label>
                <input
                  id="companies-search"
                  type="search"
                  className={formControlClass}
                  value={search}
                  placeholder={t(
                    "Dashboard.Platform.companies.filters.searchPlaceholder",
                  )}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="md:w-48">
                <label className={formLabelClass} htmlFor="companies-status">
                  {t("Dashboard.Platform.companies.columns.status")}
                </label>
                <select
                  id="companies-status"
                  className={formControlClass}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {["all", "active", "pendingSetup", "deactivated"].map((v) => (
                    <option key={v} value={v}>
                      {t(`Dashboard.Platform.companies.filters.status.${v}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:w-56">
                <label className={formLabelClass} htmlFor="companies-licence">
                  {t("Dashboard.Platform.companies.columns.licence")}
                </label>
                <select
                  id="companies-licence"
                  className={formControlClass}
                  value={licenceFilter}
                  onChange={(e) => setLicenceFilter(e.target.value)}
                >
                  {["all", "attention", "expiring", "unbilled"].map((v) => (
                    <option key={v} value={v}>
                      {t(`Dashboard.Platform.companies.filters.licence.${v}`)}
                    </option>
                  ))}
                </select>
              </div>

              {filtersApplied && (
                <button
                  type="button"
                  className={btnSecondaryClass}
                  onClick={clearFilters}
                >
                  {t("Dashboard.Platform.companies.filters.clear")}
                </button>
              )}
            </div>
          </div>

          <p className="m-0 text-fluid-sm text-ink-500" role="status">
            {/* The count is the server's total for these filters, not what this
                page happens to hold — "25 of 25" would be true and useless. */}
            {t("Dashboard.Platform.companies.filters.resultCount", {
              count: totalCount,
              total: totalCount,
            })}
          </p>

          {filtered.length === 0 ? (
            <div className={alertInfoClass}>
              <i className="bi bi-info-circle" aria-hidden="true" />
              <span>{t("Dashboard.Platform.companies.filters.noMatches")}</span>
            </div>
          ) : (
        <div className={cardClass}>
          <div className={cardBodyClass}>
            <table className={tableClass}>
              <thead className={tableHeadClass}>
                <tr>
                  <th className={tableHeaderCellClass}>
                    {t("Dashboard.Platform.companies.columns.business")}
                  </th>
                  <th className={tableHeaderCellClass}>
                    {t("Dashboard.Platform.companies.columns.owner")}
                  </th>
                  <th className={tableHeaderCellClass}>
                    {t("Dashboard.Platform.companies.columns.plan")}
                  </th>
                  <th className={tableHeaderCellClass}>
                    {t("Dashboard.Platform.companies.columns.status")}
                  </th>
                  <th className={tableHeaderCellClass}>
                    {t("Dashboard.Platform.companies.columns.licence")}
                  </th>
                  <th className={tableHeaderCellClass}>
                    {t("Dashboard.Platform.companies.columns.team")}
                  </th>
                  <th className={tableHeaderCellClass}>
                    {t("Dashboard.Platform.companies.columns.recentBookings")}
                  </th>
                  <th className={tableHeaderCellClass}>
                    {t("Dashboard.Platform.companies.columns.admin")}
                  </th>
                  {isSystemOwner && (
                    <th className={tableHeaderCellClass}>
                      {t("Dashboard.Platform.companies.columns.directory")}
                    </th>
                  )}
                  <th className={tableHeaderCellClass}>
                    <span className="sr-only">
                      {t("Dashboard.Platform.companies.columns.actions")}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className={tableBodyClass}>
                {filtered.map((company) => {
                  const state = status(company);
                  const licence = company.licenceState;
                  return (
                    <tr key={company.id} className={tableRowClass}>
                      <td
                        className={tableCellClass}
                        data-label={t(
                          "Dashboard.Platform.companies.columns.business",
                        )}
                      >
                        <span className="flex flex-col">
                          <span className="font-semibold text-ink-900">
                            {company.businessName}
                          </span>
                          <span className="text-xs text-ink-500">
                            {company.city}
                          </span>
                        </span>
                      </td>
                      <td
                        className={tableCellClass}
                        data-label={t(
                          "Dashboard.Platform.companies.columns.owner",
                        )}
                      >
                        {/* Both contact routes are links rather than text:
                            reaching a business is the reason this page exists,
                            and outreach happens by phone or by an email someone
                            writes themselves. */}
                        <span className="flex flex-col">
                          <span>{company.ownerName}</span>
                          <a
                            href={`mailto:${company.ownerEmail}`}
                            className="text-xs text-brand-600 hover:underline"
                          >
                            {company.ownerEmail}
                          </a>
                          {company.phoneNumber && (
                            <a
                              href={`tel:${company.phoneNumber}`}
                              className="text-xs text-brand-600 hover:underline"
                            >
                              {company.phoneNumber}
                            </a>
                          )}
                        </span>
                      </td>
                      <td
                        className={tableCellClass}
                        data-label={t(
                          "Dashboard.Platform.companies.columns.plan",
                        )}
                      >
                        {company.plan == null
                          ? "—"
                          : t(PlanNameKeys[company.plan])}
                      </td>
                      <td
                        className={tableCellClass}
                        data-label={t(
                          "Dashboard.Platform.companies.columns.status",
                        )}
                      >
                        <span className={`${badgeClass} ${state.className}`}>
                          {t(
                            `Dashboard.Platform.companies.status.${state.key}`,
                          )}
                        </span>
                      </td>
                      <td
                        className={tableCellClass}
                        data-label={t(
                          "Dashboard.Platform.companies.columns.licence",
                        )}
                      >
                        <span className="flex flex-col items-end gap-0.5 md:items-start">
                          <span
                            className={`${badgeClass} ${LICENCE_BADGE[licence]}`}
                          >
                            {licence === "expiring" || licence === "expired"
                              ? t(
                                  `Dashboard.Platform.companies.licence.${licence}`,
                                  { count: Math.abs(company.daysUntilExpiry) },
                                )
                              : t(
                                  `Dashboard.Platform.companies.licence.${licence}`,
                                )}
                          </span>
                          {company.expiresAt && (
                            <span className="text-xs text-ink-400">
                              {formatDate(company.expiresAt)}
                            </span>
                          )}
                        </span>
                      </td>
                      <td
                        className={tableCellClass}
                        data-label={t(
                          "Dashboard.Platform.companies.columns.team",
                        )}
                      >
                        {company.memberCount}
                      </td>
                      <td
                        className={tableCellClass}
                        data-label={t(
                          "Dashboard.Platform.companies.columns.recentBookings",
                        )}
                      >
                        <span className="flex flex-col">
                          <span>{company.bookingsLast30Days}</span>
                          <span className="text-xs text-ink-400">
                            {t("Dashboard.Platform.companies.totalBookings", {
                              count: company.totalBookings,
                            })}
                          </span>
                        </span>
                      </td>
                      <td
                        className={tableCellClass}
                        data-label={t(
                          "Dashboard.Platform.companies.columns.admin",
                        )}
                      >
                        {isSystemOwner ? (
                          <select
                            className={`${formControlClass} md:w-44`}
                            value={company.assignedAdminId ?? ""}
                            aria-label={t(
                              "Dashboard.Platform.companies.assignLabel",
                              { business: company.businessName },
                            )}
                            onChange={(e) => assign(company.id, e.target.value)}
                          >
                            <option value="">
                              {t("Dashboard.Platform.companies.unassigned")}
                            </option>
                            {admins.map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.firstName} {a.lastName}
                              </option>
                            ))}
                          </select>
                        ) : (
                          (company.assignedAdminName ??
                          t("Dashboard.Platform.companies.unassigned"))
                        )}
                      </td>
                      {/* Whether this business is advertised at /discover.
                          SystemOwner only: an Admin looks after the companies
                          assigned to them, and promoting one to every visitor
                          is a claim the platform makes, not a management task.
                          The server refuses an Admin too. */}
                      {isSystemOwner && (
                        <td
                          className={tableCellClass}
                          data-label={t(
                            "Dashboard.Platform.companies.columns.directory",
                          )}
                        >
                          <select
                            className={formControlClass}
                            value={company.directoryListing}
                            onChange={(e) =>
                              changeDirectoryListing(
                                company.id,
                                Number(e.target.value),
                              )
                            }
                            data-testid={`directory-listing-${company.id}`}
                          >
                            <option value={DirectoryListing.AUTOMATIC}>
                              {t("Dashboard.Platform.companies.directory.automatic")}
                            </option>
                            <option value={DirectoryListing.ALWAYS_LISTED}>
                              {t("Dashboard.Platform.companies.directory.always")}
                            </option>
                            <option value={DirectoryListing.NEVER_LISTED}>
                              {t("Dashboard.Platform.companies.directory.never")}
                            </option>
                          </select>

                          {/* "Automatic" alone does not say whether anybody can
                              actually find them — that depends on whether the
                              invoice is paid, which is the whole point of the
                              setting. So the row says which it currently is. */}
                          <span
                            className={`mt-1 block text-xs ${
                              company.isListedInDirectory
                                ? "text-brand-700"
                                : "text-ink-400"
                            }`}
                            data-testid={`directory-state-${company.id}`}
                          >
                            {company.isListedInDirectory
                              ? t("Dashboard.Platform.companies.directory.visible")
                              : t("Dashboard.Platform.companies.directory.hidden")}
                          </span>
                        </td>
                      )}

                      <td className={tableActionsCellClass}>
                        <span className="flex flex-wrap justify-end gap-2">
                          <button
                            type="button"
                            className={btnSecondaryClass}
                            onClick={() => openCompany(company)}
                          >
                            {t("Dashboard.Platform.companies.view")}
                          </button>
                          {/* Not offered for a business taken offline: its
                              owner erased their account, so there is nobody to
                              act as, and the API refuses it anyway. */}
                          {isSystemOwner && !company.isDeactivated && (
                            <button
                              type="button"
                              className={btnSecondaryClass}
                              onClick={() => impersonate(company)}
                              disabled={impersonatingId != null}
                              aria-label={t(
                                "Dashboard.Platform.companies.simulateLabel",
                                { business: company.businessName },
                              )}
                              title={t(
                                "Dashboard.Platform.companies.simulateLabel",
                                { business: company.businessName },
                              )}
                              data-testid={`simulate-${company.id}`}
                            >
                              <i
                                className={`bi ${
                                  impersonatingId === company.id
                                    ? "bi-hourglass-split"
                                    : "bi-person-badge"
                                }`}
                                aria-hidden="true"
                              />
                              {t("Dashboard.Platform.companies.simulate")}
                            </button>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ink-100 px-4 py-3">
              <p className="m-0 text-fluid-sm text-ink-500" role="status">
                {t("Dashboard.Platform.companies.pager.position", {
                  page,
                  totalPages,
                })}
              </p>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className={btnSecondaryClass}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading}
                >
                  {t("Dashboard.Platform.companies.pager.previous")}
                </button>
                <button
                  type="button"
                  className={btnSecondaryClass}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || loading}
                >
                  {t("Dashboard.Platform.companies.pager.next")}
                </button>
              </div>
            </div>
          )}
        </div>
          )}
        </div>
      )}

      {openFor && (
        <div className={modalOverlayClass} role="dialog" aria-modal="true">
          <div className={modalPanelClass}>
            <div className={modalHeaderClass}>
              <div className="min-w-0">
                <h2 className="m-0 truncate text-lg font-semibold text-ink-900">
                  {openFor.businessName}
                </h2>
                <p className="m-0 truncate text-xs text-ink-500">
                  {openFor.companyName}
                </p>
              </div>
              <button
                type="button"
                className={modalCloseClass}
                onClick={() => setOpenFor(null)}
                aria-label={t("Dashboard.Platform.companies.close")}
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            </div>

            <div className={modalBodyClass}>
              {detailFailed ? (
                <div className={alertErrorClass} role="alert">
                  {t("Dashboard.Platform.companies.detailError")}
                </div>
              ) : !detail ? (
                <div className="flex justify-center py-10">
                  <div className={spinnerClass} role="status" />
                </div>
              ) : (
                <div className="flex flex-col gap-5">
                  <dl className="grid gap-3 sm:grid-cols-2">
                    <Field
                      label={t("Dashboard.Platform.companies.columns.owner")}
                      value={`${detail.company.ownerName} · ${detail.company.ownerEmail}`}
                    />
                    <Field
                      label={t("Dashboard.Platform.companies.detail.phone")}
                      value={
                        detail.company.phoneNumber ? (
                          <a
                            href={`tel:${detail.company.phoneNumber}`}
                            className="text-brand-600 hover:underline"
                          >
                            {detail.company.phoneNumber}
                          </a>
                        ) : (
                          t("Dashboard.Platform.companies.detail.noPhone")
                        )
                      }
                    />
                    <Field
                      label={t("Dashboard.Platform.companies.detail.location")}
                      value={`${detail.company.city}, ${detail.company.county}`}
                    />
                    <Field
                      label={t("Dashboard.Platform.companies.columns.plan")}
                      value={
                        detail.company.plan == null
                          ? "—"
                          : t(PlanNameKeys[detail.company.plan])
                      }
                    />
                    <Field
                      label={t("Dashboard.Platform.companies.detail.joined")}
                      value={formatDate(detail.company.createdAt)}
                    />
                    <Field
                      label={t("Dashboard.Platform.companies.detail.lastBooking")}
                      value={formatDate(detail.company.lastBookingCreatedAt)}
                    />
                    <Field
                      label={t("Dashboard.Platform.companies.columns.team")}
                      value={String(detail.company.memberCount)}
                    />
                  </dl>

                  {/* The company's own report, from the company's own service,
                      so platform staff and the owner read one set of numbers.

                      It windows on the appointment's own date, while the list
                      column counts bookings by when they were *made*. Both are
                      right and they routinely disagree — a business can take
                      ten bookings today for appointments next month — so the
                      two are named apart and the difference is spelled out
                      rather than left for someone to report as a bug. */}
                  <div>
                    <h3 className="mb-1 text-sm font-semibold text-ink-900">
                      {t("Dashboard.Platform.companies.detail.reportTitle")}
                    </h3>
                    <p className="mt-0 mb-3 text-xs text-ink-500">
                      {t("Dashboard.Platform.companies.detail.reportHint")}
                    </p>
                    <div className={statsGridClass}>
                      <Metric
                        label={t("Dashboard.Platform.companies.detail.total")}
                        value={detail.reports?.summary?.totalBookings ?? 0}
                      />
                      <Metric
                        label={t(
                          "Dashboard.Platform.companies.detail.completed",
                        )}
                        value={detail.reports?.summary?.completedBookings ?? 0}
                      />
                      <Metric
                        label={t(
                          "Dashboard.Platform.companies.detail.cancelled",
                        )}
                        value={detail.reports?.summary?.cancelledBookings ?? 0}
                      />
                      <Metric
                        label={t(
                          "Dashboard.Platform.companies.detail.customers",
                        )}
                        value={detail.reports?.summary?.uniqueCustomers ?? 0}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className={modalFooterClass}>
              <button
                type="button"
                className={btnSecondaryClass}
                onClick={() => setOpenFor(null)}
              >
                {t("Dashboard.Platform.companies.close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value }) => (
  <div>
    <dt className="text-xs font-semibold text-ink-500">{label}</dt>
    <dd className="m-0 text-fluid-sm break-words text-ink-900">{value}</dd>
  </div>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

const Metric = ({ label, value }) => (
  <div className="rounded-lg border border-ink-200 p-3">
    <p className="m-0 text-xl font-bold text-ink-900">{value}</p>
    <p className="m-0 text-xs text-ink-500">{label}</p>
  </div>
);

Metric.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

export default Companies;
